import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, PrimaryButton, Modal, Field, TextInput, Select, useFetch } from "../../components/crm/ui";
import { Plus, Trash2, Edit3, AlertTriangle } from "lucide-react";

const EMPTY = { code: "", name: "", material_group_id: "", vendor_id: "",
  warranty_days: 0, quantity: 0, min_quantity: 0, unit_price: 0, description: "" };

export default function Materials() {
  const { data, reload } = useFetch("/materials");
  const { data: groups } = useFetch("/material-groups");
  const { data: vendors } = useFetch("/vendors");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  function startCreate() { setEditing(null); setForm(EMPTY); setOpen(true); }
  function startEdit(m) { setEditing(m); setForm({ ...m }); setOpen(true); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form,
      warranty_days: Number(form.warranty_days),
      quantity: Number(form.quantity),
      min_quantity: Number(form.min_quantity),
      unit_price: Number(form.unit_price),
    };
    if (!payload.material_group_id) delete payload.material_group_id;
    if (!payload.vendor_id) delete payload.vendor_id;
    try {
      if (editing) await api.put(`/materials/${editing.id}`, payload);
      else await api.post("/materials", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  }

  async function remove(m) {
    if (!window.confirm(`Delete ${m.code}?`)) return;
    await api.delete(`/materials/${m.id}`); toast.success("Deleted"); reload();
  }

  return (
    <div data-testid="materials-page">
      <PageHeader overline="Master Data" title="Materials"
        action={<PrimaryButton onClick={startCreate} testid="create-material-btn"><Plus className="w-4 h-4" /> New Material</PrimaryButton>} />
      <div className="border border-[#E2E4DC] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-[0.25em] text-[#4A5568] border-b border-[#E2E4DC]">
              <th className="px-4 py-3">Code</th><th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Group</th><th className="px-4 py-3">Vendor</th>
              <th className="px-4 py-3 text-right">Qty</th><th className="px-4 py-3 text-right">Min</th>
              <th className="px-4 py-3 text-right">Price</th><th className="px-4 py-3 text-right">Warranty</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.map((m) => {
              const low = m.quantity < m.min_quantity;
              const grp = groups.find((g) => g.id === m.material_group_id);
              const ven = vendors.find((v) => v.id === m.vendor_id);
              return (
                <tr key={m.id} className="border-b border-[#E2E4DC] hover:bg-[#F4F5F0]" data-testid={`mat-row-${m.code}`}>
                  <td className="px-4 py-3 font-mono">{m.code}</td>
                  <td className="px-4 py-3">{m.name}</td>
                  <td className="px-4 py-3 text-xs text-[#4A5568]">{grp?.name || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#4A5568]">{ven?.name || "—"}</td>
                  <td className={"px-4 py-3 text-right font-mono " + (low ? "text-red-600 font-semibold" : "")}>
                    {low && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                    {m.quantity}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{m.min_quantity}</td>
                  <td className="px-4 py-3 text-right font-mono">₹{m.unit_price.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{m.warranty_days}d</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => startEdit(m)} className="p-1.5 hover:text-[#1F4E2C]"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => remove(m)} className="p-1.5 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
            {data.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-[#4A5568]">No materials yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Material" : "New Material"} wide>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Code *"><TextInput required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="mat-code" disabled={!!editing} /></Field>
          <Field label="Name *"><TextInput required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Material Group">
            <Select value={form.material_group_id} onChange={(e) => setForm({ ...form, material_group_id: e.target.value })}>
              <option value="">— None —</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </Select>
          </Field>
          <Field label="Vendor">
            <Select value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}>
              <option value="">— None —</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </Select>
          </Field>
          <Field label="Warranty (days)"><TextInput type="number" value={form.warranty_days} onChange={(e) => setForm({ ...form, warranty_days: e.target.value })} /></Field>
          <Field label="Quantity"><TextInput type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></Field>
          <Field label="Minimum Quantity"><TextInput type="number" value={form.min_quantity} onChange={(e) => setForm({ ...form, min_quantity: e.target.value })} /></Field>
          <Field label="Unit Price (₹)"><TextInput type="number" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} /></Field>
          <div className="md:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#4A5568]">Cancel</button>
            <PrimaryButton type="submit" disabled={saving} testid="mat-save-btn">{saving ? "Saving…" : "Save"}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
